import { useFormContext } from "react-hook-form";
import { Alert, Box, Button, List, Stack, Typography as T, useTheme } from "@mui/material";
import IngestFormErrors, { ListItemError } from "datasets/pages/tables/components/IngestFormErrors";
import useIngestContext from "datasets/pages/tables/contexts/ingest";
import { TABLE_EXISTS_WARNING } from "datasets/pages/tables/utils/tableExists";
import { resolveRouterPath } from "files/hooks/useFiles";
import prettyBytes from "pretty-bytes";

const ReviewRow = ({ label, value, error }: { label: string; value: string; error?: string }) => {
  const theme = useTheme();

  return (
    <Box
      alignItems="flex-start"
      display="flex"
      gap={3}
      py={1}
      sx={{ borderBottom: "1px solid #e0e0e7", "&:last-of-type": { borderBottom: "unset" } }}
    >
      <Box minWidth={120}>
        <T fontSize={14} sx={{ opacity: 0.6 }}>
          {`${label}:`}
        </T>
      </Box>
      <Box flex={1}>
        <T fontSize={14}>{value}</T>
        {error && (
          <T variant="caption" color={theme.palette.attention.main} display="block">
            {error}
          </T>
        )}
      </Box>
    </Box>
  );
};

const ReviewDetails = () => {
  const { formState, watch } = useFormContext();
  const { errors, tableExists } = useIngestContext();
  const { errors: formErrors } = formState;
  const columns = watch("metadata_config.columns");

  const showErrors = (errors && errors.length > 0) || Object.keys(formErrors).length > 0;
  const hasValidColumns = columns && columns.length > 0;

  const fileSize = watch("file_size");
  const sourceData = watch("platform_storage_key");
  const metadata = watch("metadata_config");

  const items = [
    { label: "Source Location", value: "Amazon S3", error: "" },
    {
      label: "Source Data",
      value: resolveRouterPath(sourceData) ?? "(Table name / File name)",
      error: "",
    },
    {
      label: "Destination",
      value: `${metadata.database_schema}.${metadata.table}`,
      error: tableExists ? TABLE_EXISTS_WARNING : "",
    },
    {
      label: "Size",
      value: fileSize ? prettyBytes(fileSize) : "",
      error: "",
    },
  ];

  return (
    <>
      {showErrors && (
        <>
          {errors.length > 0 && (
            <Alert severity="error" variant="outlined" sx={{ width: "100%" }}>
              <List disablePadding>
                {errors.map((err: string, i: number) => (
                  <ListItemError key={i} error={err} />
                ))}
              </List>
            </Alert>
          )}
          <IngestFormErrors />
        </>
      )}

      <Stack
        sx={{
          border: "1px solid #e0e0e7",
          borderRadius: 1.5,
          paddingX: 2,
          width: "100%",
        }}
      >
        {items.map((row, i) => (
          <ReviewRow key={i} {...row} />
        ))}
      </Stack>
      <Button
        type="submit"
        variant="contained"
        disabled={!formState.isValid || !hasValidColumns}
        loading={formState.isSubmitting}
      >
        Add Table
      </Button>
    </>
  );
};

export default ReviewDetails;
